import { measure } from '@/components/Analytics/Analytics';
import CollectionCardBadges from '@/components/Collections/CollectionCard/CollectionCardBadges/CollectionCardBadges';
import ArrowLeftIcon from '@/components/Icon/ArrowRightIcon';
import CollectionsIcon from '@/components/Icon/CollectionsIcon';
import MainUserMedia from '@/components/MainUserMedia/MainUserMedia';
import SectionBackground from '@/components/SectionBackground/SectionBackground';
import CurrentDateContext from '@/context/CurrentDateContext';
import RememberScrollPositionContext from '@/context/RememberScrollPositionContext';
import useCards from '@/hooks/cards/useCards';
import useCollections from '@/hooks/collections/useCollections';
import useSpecies from '@/hooks/species/useSpecies';
import useHaptics from '@/hooks/useHaptics';
import {
	CalendarAttributes,
	SpeciesAttributes,
	StrapiEntry,
} from '@/lib/apiTypes';
import { motion, useReducedMotion } from 'framer-motion';
import {
	CSSProperties,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import SingleCalendarSpeciesVideo from './SingleCalendarSpeciesVideo';
import SpeciesOfTheWeekLabel from './SpeciesOfTheWeekLabel';

const SingleCalendarSpecies = ({
	calendarEntry,
	species,
	isSpeciesOfTheWeek,
}: {
	calendarEntry: StrapiEntry<CalendarAttributes>;
	species: StrapiEntry<SpeciesAttributes> | undefined;
	isSpeciesOfTheWeek: boolean;
}) => {
	const navigate = useNavigate();
	const { withOnButtonPress } = useHaptics();

	const reduceMotion = useReducedMotion();

	const selfRef = useRef<HTMLLIElement>(null);
	const rememberScrollPosition = useContext(RememberScrollPositionContext);
	const { currentDate } = useContext(CurrentDateContext);

	const { getMainImageById, getSpeciesRouteById } = useSpecies();
	const { findCollectionsOfSpeciesId } = useCollections();
	const { getCardBySpeciesId } = useCards();

	// Scroll item in view if species of the week
	useLayoutEffect(() => {
		// If there is a scroll position with key tab-calendar higher than 0, do not auto scroll anymore
		if (rememberScrollPosition.get('tab-calendar') ?? 0 > 0) return;

		if (isSpeciesOfTheWeek && selfRef.current) {
			selfRef.current?.scrollIntoView({
				behavior: 'instant',
				block: 'center',
				inline: 'center',
			});
		}
	}, [selfRef, isSpeciesOfTheWeek, rememberScrollPosition, reduceMotion]);

	// Get random mask for photo
	const randomMask: CSSProperties = useMemo(() => {
		const maskNumbers = [3, 4, 5, 6];
		const randomMaskNumber =
			maskNumbers[Math.floor(Math.random() * maskNumbers.length)];
		return {
			WebkitMaskImage: `url("/assets/masks/blob_square_${randomMaskNumber}.svg")`,
			maskImage: `url("/assets/masks/blob_square_${randomMaskNumber}.svg")`,
			maskSize: 'fill',
			maskRepeat: 'no-repeat',
		};
	}, []);

	// Get URL
	const url = useMemo(
		() => getSpeciesRouteById(species?.id ?? 0),
		[getSpeciesRouteById, species],
	);

	// Get image
	const mainImage = useMemo(
		() => getMainImageById(species?.id ?? 0),
		[getMainImageById, species],
	);

	// Get if species is collected
	const isCollected = useMemo(
		() => !!getCardBySpeciesId(species?.id ?? 0),
		[getCardBySpeciesId, species],
	);

	// Check if species info is available
	if (!species || !url || !mainImage) return null;

	// Check if species is in the past
	const isInPastOrToday =
		new Date(calendarEntry.attributes.date) <= currentDate;

	// Get collection name
	const collectionName = findCollectionsOfSpeciesId(species.id)[0]?.attributes
		.titleLong;

	// Default subtitles
	const defaultCollectedSubtitle = 'In jouw verzameling!';
	const defaultUnCollectedSubtitle = isSpeciesOfTheWeek
		? "Tik hier en spot 'm!"
		: 'Tik hier om te spotten!';

	// Choose subtitle based on if the species is in the collection
	const defaultSubtitle = isCollected
		? defaultCollectedSubtitle
		: defaultUnCollectedSubtitle;

	// Title is short (can have a bigger font size) if it's 10 or less characters
	const isTitleShort = species.attributes.titleShort.length <= 10;

	// Show video if it's available, species of the week and user has not collected it yet
	const showVideo =
		species.attributes.videoMain && isSpeciesOfTheWeek && !isCollected;

	return (
		<li
			ref={selfRef}
			className="text-white transition-all active:scale-y-90">
			{isSpeciesOfTheWeek && <SpeciesOfTheWeekLabel />}
			<a
				className="relative block"
				href={url}
				onClick={(event) => {
					event.preventDefault();
					measure(
						`calendar-species-${species.id}-${species.attributes.slug}`,
						'navigation',
					);
					navigate(url);
				}}
				{...withOnButtonPress}>
				<SectionBackground
					className={
						isInPastOrToday ? 'text-primary' : 'text-secondary'
					}
				/>
				<div className="relative mx-auto flex w-full max-w-[40rem] flex-row-reverse items-center">
					<div
						className={`grow p-2 xxs:p-4 ${
							isSpeciesOfTheWeek ? 'py-12' : ''
						}`}>
						<h3
							className={`font-brand ${
								isTitleShort
									? 'text-3xl first-letter:text-4xl'
									: 'text-2xl first-letter:text-3xl'
							}
								sm:text-4xl sm:first-letter:text-5xl lg:text-5xl lg:first-letter:text-6xl`}>
							{species.attributes.titleShort}
							<motion.div
								className="absolute end-1 top-1/2 hidden gap-4 xxs:grid xs:end-2"
								initial={{
									scale: isSpeciesOfTheWeek ? 0.75 : 0.5,
									y: '-50%',
								}}
								// If species of the week and not collected and if not reduced motion, animate the arrow from left to right to grab attention
								whileInView={{
									x:
										isSpeciesOfTheWeek &&
										!isCollected &&
										!reduceMotion
											? [0, '-0.5rem']
											: 0,
									transition: {
										repeat: 6,
										repeatType: 'reverse',
										ease: 'backOut',
										duration: 1.5,
									},
								}}>
								<ArrowLeftIcon />
							</motion.div>
						</h3>
						<div>
							{isSpeciesOfTheWeek && (
								<p className="text-lg">{defaultSubtitle}</p>
							)}
							<p className="flex flex-wrap items-center gap-1">
								{!!collectionName && (
									<>
										<CollectionsIcon className="-ms-0.5 hidden h-4 xxs:block lg:h-4" />{' '}
										{collectionName}
									</>
								)}
							</p>
						</div>
					</div>
					<div className="relative ms-1 shrink-0 xxs:ms-3">
						<div
							style={{ ...randomMask }}
							className={`relative translate-y-1 bg-white ${
								isSpeciesOfTheWeek
									? 'h-32 w-16 xxs:w-24 lg:h-44 lg:w-36'
									: 'size-16 xxs:size-24 lg:size-36'
							}`}>
							<img
								src={mainImage.thumbnailUrl}
								alt={mainImage.alt}
								loading="lazy"
								className="size-full scale-90 object-contain grayscale-[85%]"
							/>
							{showVideo && (
								<SingleCalendarSpeciesVideo
									video={species.attributes.videoMain}
								/>
							)}
							<MainUserMedia
								speciesId={species.id}
								isThumbnail
								alt="Laatste foto uit jouw verzameling"
								className="absolute inset-0 size-full scale-110 object-cover"
							/>
						</div>
						<CollectionCardBadges
							species={species}
							alignment="side"
						/>
					</div>
				</div>
			</a>
		</li>
	);
};

export default SingleCalendarSpecies;
